import axios from "axios";
import React, { useEffect, useCallback, useState, Fragment } from "react";
import { useLocation, useParams } from "react-router-dom";
// import { useMedia } from "react-use";
import dayjs from "dayjs";

import {
  usePreviewJoin,
  selectLocalPeer,
  useHMSStore,
  selectIsLocalVideoEnabled,
  useAVToggle,
  selectRemotePeers,
  useHMSActions,
} from "@100mslive/react-sdk";
import {
  Text,
  Loading,
  useTheme,
  Avatar,
  Flex,
  textEllipsis,
  Box,
  // config as cssConfig,
  StyledVideoTile,
  useBorderAudioLevel,
  Video,
} from "@100mslive/roomkit-react";
import PreviewControls from "./PreviewControls";
import PcPreviewScreen from "./PcPreviewScreen";
import EventsPreviewScreen from "./EventsPreviewScreen";
import DefaultPreviewScreen from "./DefaultPreviewScreen";
import TileConnection from "../Connection/TileConnection";
import PreviewName from "./PreviewName";
import {
  useUserPreferences,
  UserPreferencesKeys,
  defaultPreviewPreference,
} from "../hooks/useUserPreferences";
import { APP_DATA } from "../../common/constants";
import { ToastManager } from "../Toast/ToastManager";
import { isMobile } from "react-device-detect";

const PreviewJoin = ({
  token,
  onJoin,
  env,
  skipPreview,
  initialName,
  localPeerRole,
  userData,
}) => {
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );
  const [uiSettingsPreference = {}] = useUserPreferences(
    UserPreferencesKeys.UI_SETTINGS
  );
  const { role: userRole } = useParams(); // from the url
  const hmsActions = useHMSActions();

  const [name, setName] = useState("");
  const search = useLocation().search;
  // YOCKET INFO
  const username = new URLSearchParams(search).get("name");
  const term = new URLSearchParams(search).get("term");
  const year = new URLSearchParams(search).get("year");
  const degree = new URLSearchParams(search).get("degree");
  const phone = new URLSearchParams(search).get("phone");
  const role = new URLSearchParams(search).get("role");
  const email = new URLSearchParams(search).get("email");
  const profileImage = new URLSearchParams(search).get("pfp");
  const user_uuid = new URLSearchParams(search).get("uuid");
  const stage = new URLSearchParams(search).get("stage");
  const stageAssistance = new URLSearchParams(search).get("stageAssistance");
  const countries = new URLSearchParams(search).get("countries");
  const education = new URLSearchParams(search).get("education");

  const meetingType = new URLSearchParams(search).get("meetingType");
  const joiningLink = new URLSearchParams(search).get("joining_link");

  const event_id = new URLSearchParams(search).get("event_id");
  const event_venue_id = new URLSearchParams(search).get("event_venue_id");
  const english_test_scores = new URLSearchParams(search).get(
    "english_test_scores"
  );
  const aptitude_test_scores = new URLSearchParams(search).get(
    "aptitude_test_scores"
  );

  const isWebinar = meetingType === "webinar";
  const isWebinarGuest = isWebinar && localPeerRole?.includes("guest");
  const isPCVC = meetingType === "pc_vc";

  const roomMetadata = {
    year: year,
    degree: degree,
    phone: phone,
    term: term,
    role: role,
    email: email,
    profileImage: profileImage,
    user_uuid: user_uuid,
    stage: stage,
    stageAssistance: stageAssistance,
    countries: countries,
    education: education,
    english_test_scores: english_test_scores,
    aptitude_test_scores: aptitude_test_scores,
    page_url: joiningLink ? atob(joiningLink) : "",
    ...userData,
  };

  roomMetadata.meetingType = meetingType;

  if (meetingType === "webinar") {
    roomMetadata.event_id = event_id;
    roomMetadata.event_venue_id = event_venue_id;
  }

  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio } =
    useAVToggle();
  const [previewError, setPreviewError] = useState(false);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [micPermissionsDenied, setMicPermissionsDenied] = useState(false);
  const { enableJoin, preview, join } = usePreviewJoin({
    name,
    token,
    initEndpoint: env ? `https://${env}-init.100ms.live/init` : undefined,
    initialSettings: {
      isAudioMuted: skipPreview || previewPreference.isAudioMuted,
      isVideoMuted:
        !isWebinarGuest && (skipPreview || previewPreference.isVideoMuted),
    },
    metadata: JSON.stringify(roomMetadata),
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        setPreviewError(true);
      }
    },
  });

  const [eventTime, setEventTime] = useState("");

  React.useEffect(() => {
    if (!event_id || !event_venue_id) {
      return;
    }

    const config = {
      method: "get",
      url: `${process.env.REACT_APP_YOCKET_BACKEND}/events/${event_id}`,
      headers: {
        "Content-Type": "application/json",
      },
    };

    axios(config)
      .then(response => {
        const startingTime = response?.data?.data[0]?.starting_time;

        const formattedTimeInIST = dayjs
          .utc(startingTime)
          .tz("Asia/Kolkata")
          .format("Do MMM, YYYY | h.mm A [IST Onwards]");

        setEventTime(formattedTimeInIST);
      })
      .catch(error => {
        console.warn(
          "Event fetch error",
          error?.response?.status || error?.message
        );
      });
  }, [event_id, event_venue_id]);

  const checkMicrophoneAccess = async () => {
    try {
      setTimeout(() => {
        if (!isMobile) {
          setShowMicPermissionModal(true);
        }
      }, 1000);

      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (e) {
      setMicPermissionsDenied(true);
      return false;
    }
  };

  const savePreferenceAndJoin = useCallback(async () => {
    const micPermissiongranted = await checkMicrophoneAccess();
    if (!micPermissiongranted) {
      return;
    }

    setPreviewPreference({
      name,
      isAudioMuted: !isLocalAudioEnabled,
      isVideoMuted: !isLocalVideoEnabled,
    });

    //#region guest settings on join
    if (!localPeerRole.includes("host")) {
      isLocalAudioEnabled &&
        toggleAudio() &&
        ToastManager.addToast({ title: "You're muted" });

      const updatedUiSettings = {
        ...uiSettingsPreference,
        [APP_DATA.subscribedNotifications]: {
          ...uiSettingsPreference[APP_DATA.subscribedNotifications],
          PEER_JOINED: false,
          PEER_LEFT: false,
          METADATA_UPDATED: false,
        },
      };
      hmsActions.setAppData(
        APP_DATA.subscribedNotifications,
        updatedUiSettings[APP_DATA.subscribedNotifications],
        true
      );
    }
    //#endregion guest settings on join
    join();
    !localPeerRole.includes("host") && isLocalAudioEnabled && toggleAudio();
    onJoin && onJoin();
    if (event_id && event_venue_id && (userData?.user_uuid || user_uuid)) {
      // since we are already triggering this from single events page join now cta
      // fetch(process.env.REACT_APP_YOCKET_BACKEND + "/events/attendance", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify({
      //     event_id,
      //     event_venue_id,
      //     uuid: userData?.user_uuid || user_uuid,
      //   }),
      // });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setPreviewPreference,
    name,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    join,
    localPeerRole,
    toggleAudio,
    onJoin,
    event_id,
    event_venue_id,
    userData?.user_uuid,
    user_uuid,
  ]);

  const scheduleCall = async () => {
    const url = `https://yocket.com/feed/booking?source=events`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (token) {
      setName((userRole === "guest" && userData?.name) || username);
      if (skipPreview) {
        savePreferenceAndJoin();
      } else {
        preview();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, skipPreview, username, userData?.name]);

  return (
    <div className="min-h-screen w-full">
      {showMicPermissionModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white m-4 sm:m-0 rounded-xl w-[528px] h-auto p-4 sm:p-12 z-[61] flex flex-col items-center text-center gap-6">
            <img
              src="/permission-request-modal-illustration.webp"
              alt="enable-microphone"
              height="180"
              width="164"
            />
            <div className="text-center space-y-3">
              <p className="text-base sm:text-2xl font-bold">
                Please allow the microphone access to speak with your advisor
              </p>
              <p className="text-xs sm:text-base text-yocket-neutral-500">
                You won’t be able to join the meet without it
              </p>
            </div>
          </div>
        </div>
      )}
      {micPermissionsDenied && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white m-4 sm:m-0 rounded-xl w-[528px] h-auto p-4 sm:p-12 z-[61] flex flex-col items-center text-center gap-6">
            <video
              autoPlay
              loop
              muted
              controlsList="nodownload"
              className="w-auto sm:w-[432px] h-auto rounded-xl"
            >
              <source
                src={`${
                  process.env.REACT_APP_CLOUDFRONT_CDN_DOMAIN
                }/videos/micPerm${isMobile ? "Mobile" : ""}.mp4`}
                type="video/mp4"
              ></source>
              Your browser does not support HTML video.
            </video>
            <div className="flex flex-col gap-3 items-center">
              <p className="text-base sm:text-2xl font-bold">
                Sorry, you cannot talk to the advisor without the mic. Please
                turn it on! 👆
              </p>

              <hr className="h-px bg-yocket-neutral-200 mx-4 w-full" />

              <div className="flex items-center justify-center gap-1 sm:gap-2 text-yocket-neutral-500 text-xs sm:text-sm">
                <span>Still facing a problem?</span>
                <div
                  className="flex items-center gap-0.5 text-yocket-orange-700 font-bold cursor-pointer"
                  onClick={scheduleCall}
                >
                  <span>Schedule a Call</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Flex
        align="center"
        justify="center"
        css={{
          "@sm": { width: "100%" },
          flexDirection: "column",
        }}
      >
        {/* This will be shown just to Hosts */}
        {localPeerRole && localPeerRole.includes("host") ? (
          <div className="flex flex-col p-4">
            <PreviewTile
              name={name}
              error={previewError}
              localPeerRole={localPeerRole}
              style={{
                backgroundColor: "none",
              }}
            />
            <Flex
              align="center"
              justify="center"
              css={{
                "@sm": { width: "100%" },
                flexDirection: "row",
                width: "100%",
                marginTop: "2px",
              }}
            >
              <PreviewControls
                enableJoin={enableJoin}
                savePreferenceAndJoin={savePreferenceAndJoin}
                enableVideoToggle={
                  !(isWebinar && localPeerRole.includes("guest"))
                }
              />
              <PreviewName
                name={name}
                onChange={setName}
                enableJoin={enableJoin}
                localPeerRole={localPeerRole}
                onJoin={savePreferenceAndJoin}
              />
            </Flex>
          </div>
        ) : isWebinar ? (
          <EventsPreviewScreen
            eventTime={eventTime}
            name={name}
            enableJoin={enableJoin}
            savePreferenceAndJoin={savePreferenceAndJoin}
          />
        ) : isPCVC ? (
          <PcPreviewScreen
            name={name}
            enableJoin={enableJoin}
            savePreferenceAndJoin={savePreferenceAndJoin}
          />
        ) : (
          <DefaultPreviewScreen
            name={name}
            enableJoin={enableJoin}
            savePreferenceAndJoin={savePreferenceAndJoin}
            isPCVCPreview={isPCVC}
          />
        )}
      </Flex>
    </div>
  );
};

// const GuestPreviewContainer = ({ properties }) => {
//   const peers = useHMSStore(selectRemotePeers);
//   const hostPeers = getHostDetails(peers);
//   const firstPeer = hostPeers[0];
//   const mediaQueryMd = cssConfig.media.md;
//   const isMobile = useMedia(mediaQueryMd);
//   return (
//     <div className="flex flex-col mt-16 sm:mt-0">
//       {/* Profile pic and Header text */}
//       <div className="flex flex-col-reverse sm:flex-row">
//         {/* Big text*/}
//         {firstPeer ? (
//           <>
//             {/* Profile Picture */}
//             <ProfilePicture peer={firstPeer} />
//             <div className="w-full sm:w-2/3 host_detail_text flex flex-col space-y-3 px-10 py-6 sm:py-16">
//               <p className="text-[#E37712] text-sm sm:text-xl font-bold text-center sm:text-left">
//                 YOUR ADVISOR IS HERE
//               </p>
//               <p className="text-[#2B3340] text-xl sm:text-4xl font-black text-center sm:text-left">
//                 {firstPeer.name.split(" ")[0]} is ready to answer all your
//                 questions!
//               </p>
//             </div>
//           </>
//         ) : (
//           <>
//             <div className="flex flex-col">
//               <div className="flex flex-col-reverse sm:flex-row">
//                 {!isMobile ? (
//                   <NoHostPicture className="h-full w-full sm:w-1/3" />
//                 ) : (
//                   <></>
//                 )}
//                 <div className="sm:w-2/3 grid grid-cols-1 content-between">
//                   <div className="w-full host_detail_text flex flex-col space-y-3 px-10 py-10 sm:pt-24 sm:py-0">
//                     <p className="text-[#2B3340] text-xl leading-7 sm:leading-[52px] sm:text-[40px] font-bold sm:font-black text-center sm:text-start">
//                       Get all your questions answered right away!
//                     </p>
//                   </div>
//                   {isMobile ? (
//                     <NoHostPicture className="h-full w-full sm:w-1/3" />
//                   ) : (
//                     <></>
//                   )}
//                   <div className="w-full flex space-x-2 items-center py-4 pl-4 sm:pl-48 pr-4 rounded-br-lg h-fit">
//                     <PreviewName
//                       name={properties.name}
//                       onChange={properties.setName}
//                       enableJoin={properties.enableJoin}
//                       localPeerRole={properties.localPeerRole}
//                       onJoin={properties.savePreferenceAndJoin}
//                     />
//                     <PreviewControls
//                       enableJoin={properties.enableJoin}
//                       savePreferenceAndJoin={properties.savePreferenceAndJoin}
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </>
//         )}
//       </div>
//       {/* Host name and Details */}
//       {firstPeer ? (
//         <div className="flex flex-col sm:flex-row">
//           {/* host Details*/}
//           <div className="flex flex-col py-5 mx-16 sm:mx-0 sm:pl-5 sm:w-1/3 border border-[#E3E8EE] sm:border-0 rounded-md">
//             <p className="text-base font-bold text-[#2B3340] text-center sm:text-left">
//               {firstPeer.name}
//             </p>
//             <p className="text-base font-medium text-[#697386] text-center sm:text-left">
//               Student Advisor at Yocket
//             </p>
//           </div>
//           {/* Controls */}
//           <div className="w-full sm:w-2/3 flex space-x-2 items-center sm:border sm:border-l-[#E3E8EE] py-6 sm:py-4 px-4 sm:pl-48 rounded-br-lg">
//             <PreviewName
//               name={properties.name}
//               onChange={properties.setName}
//               enableJoin={properties.enableJoin}
//               localPeerRole={properties.localPeerRole}
//               onJoin={properties.savePreferenceAndJoin}
//             />
//             <PreviewControls
//               enableJoin={properties.enableJoin}
//               savePreferenceAndJoin={properties.savePreferenceAndJoin}
//             />
//           </div>
//         </div>
//       ) : (
//         <></>
//         // <div className="flex flex-col sm:flex-row">
//         //   {/* host Details*/}
//         //   <div className="flex flex-col py-3 mx-16 sm:mx-0 sm:pl-5 sm:w-1/3 border border-[#E3E8EE] sm:border-0 rounded-md">
//         //     <p className="text-base font-bold text-[#2B3340] text-center sm:text-left">
//         //       Dummy User
//         //     </p>
//         //     <p className="text-base font-medium text-[#697386] text-center sm:text-left">
//         //       Student Advisor at Yocket
//         //     </p>
//         //   </div>
//         //   <div className="w-full sm:w-2/3 flex space-x-2 items-center py-4 pl-4 sm:pl-48 pr-4 rounded-br-lg">
//         //     <PreviewName
//         //       name={properties.name}
//         //       onChange={properties.setName}
//         //       enableJoin={properties.enableJoin}
//         //       localPeerRole={properties.localPeerRole}
//         //       onJoin={properties.savePreferenceAndJoin}
//         //     />
//         //     <PreviewControls
//         //       enableJoin={properties.enableJoin}
//         //       savePreferenceAndJoin={properties.savePreferenceAndJoin}
//         //     />
//         //   </div>
//         // </div>
//       )}
//     </div>
//   );
// };

// const ProfilePicture = ({ peer }) => {
//   return (
//     <div className="relative profile_pic w-full px-16 sm:px-0 sm:w-1/3">
//       <div className="h-full">
//         {peer &&
//         peer.metadata &&
//         Object.keys(JSON.parse(peer?.metadata || "{}")).length &&
//         JSON.parse(peer?.metadata || "{}").profileImage ? (
//           <img
//             className="h-full w-full rounded-t-md sm:rounded-t-none sm:rounded-tl-lg"
//             alt="profile_image"
//             src={JSON.parse(peer?.metadata || "{}").profileImage}
//             height="60px"
//             width="60px"
//           ></img>
//         ) : (
//           <div className="bg-[#E3E8EE] p-16 sm:rounded-tl-lg h-full">
//             <Avatar
//               name={peer.name}
//               css={{
//                 p: "$4",
//               }}
//             />
//           </div>
//         )}
//       </div>
//       <div className="absolute flex space-x-1 items-center inset-y-0 right-20 sm:right-3 mt-4 bg-gradient-to-b from-[#E37712] to-[#D9534F] w-fit h-fit px-2 py-1 rounded-sm font-bold text-yocket-neutral-0 text-xs">
//         <div className="relative flex w-3 h-3 items-center bg-gradient-to-b from-[#E37712] to-[#D9534F] rounded-full border-2 border-white">
//           <div className="absolute bg-yocket-neutral-0 inset-x-0 mx-auto rounded-sm h-[4px] w-[4px]"></div>
//         </div>
//         <p>LIVE</p>
//       </div>
//     </div>
//   );
// };

// const NoHostPicture = ({ peer }) => {
//   const mediaQueryMd = cssConfig.media.md;
//   const isMobile = useMedia(mediaQueryMd);
//   return (
//     <div className="flex bg-yocket-neutral-0 sm:bg-[#FDF2E0] items-center mx-auto sm:rounded-l-xl">
//       <img
//         className="sm:h-full sm:w-full rounded-t-md sm:rounded-t-none sm:rounded-tl-lg sm:px-16 py-11 sm:py-24"
//         height="154px"
//         width="154px"
//         alt="profile_image"
//         src={
//           isMobile
//             ? require("../../images/default-live-user-mobile.svg")
//             : require("../../images/default-live-user.svg")
//         }
//       ></img>
//     </div>
//   );
// };

const PreviewTile = ({ name, error, localPeerRole }) => {
  const localPeer = useHMSStore(selectLocalPeer);
  const peers = useHMSStore(selectRemotePeers);
  const borderAudioRef = useBorderAudioLevel(localPeer?.audioTrack);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const hostPeers = peers?.filter(ele => ele.roleName.includes("host")) || [];
  const {
    aspectRatio: { width, height },
  } = useTheme();
  return (
    <StyledVideoTile.Container
      css={{
        aspectRatio: width / height,
        width: "unset",
        height: "min(360px, 60vh)",
        backgroundColor: "rgba(242, 244, 247, 0.9)",
        mt: "$12",
      }}
      ref={borderAudioRef}
    >
      {localPeer ? (
        <>
          <TileConnection name={name} peerId={localPeer.id} hideLabel={true} />
          {isVideoOn ? (
            <Video
              mirror={true}
              trackId={localPeer.videoTrack}
              data-testid="preview_tile"
            />
          ) : (
            <></>
          )}
          {!isVideoOn ? (
            <>
              {localPeerRole.includes("host") ? (
                <StyledVideoTile.AvatarContainer>
                  <Avatar name={name} data-testid="preview_avatar_tile" />
                  <Text css={{ ...textEllipsis("75%") }} variant="body2">
                    {name}
                  </Text>
                </StyledVideoTile.AvatarContainer>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "12px",
                    height: "inherit",
                    overflowY: "auto",
                    width: "100%",
                  }}
                >
                  {hostPeers.length ? (
                    <Box
                      css={{
                        display: "grid",
                        size: "100%",
                        columnGap: "4px",
                        gridTemplateColumns: "auto",
                      }}
                    >
                      <Text
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          letterSpacing: "-0.6px",
                          textAlign: "center",
                        }}
                      >
                        You don't have to wait till the last moment. Our
                        advisors are waiting on the call to help clear all your
                        study abroad doubts.{" "}
                      </Text>
                      {peers.map(peer =>
                        peer.roleName.includes("host") ? (
                          <div
                            key={peer.id}
                            style={{
                              paddingRight: "16px",
                              paddingLeft: "16px",
                              paddingTop: "8px",
                              paddingBottom: "8px",
                              marginTop: "8px",
                              lineHeight: "12px",
                            }}
                          >
                            {Object.keys(JSON.parse(peer?.metadata || "{}"))
                              .length ? (
                              <>
                                {JSON.parse(peer?.metadata || "{}").role ? (
                                  <Flex
                                    direction="row"
                                    gap="2"
                                    style={{ placeItems: "end" }}
                                  >
                                    <div>
                                      {JSON.parse(peer?.metadata || "{}")
                                        .profileImage ? (
                                        <img
                                          alt="profile_image"
                                          src={
                                            JSON.parse(peer?.metadata || "{}")
                                              .profileImage
                                          }
                                          height="60px"
                                          width="60px"
                                          style={{
                                            borderRadius: "2rem",
                                          }}
                                        ></img>
                                      ) : (
                                        <Avatar
                                          name={peer.name}
                                          css={{
                                            p: "$4",
                                          }}
                                        />
                                      )}
                                    </div>
                                    <p
                                      style={{
                                        fontSize: "14px",
                                        color: "#2B3340",
                                        marginTop: "8px",
                                      }}
                                    >
                                      {peer.name},{" "}
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          color: "#697386",
                                        }}
                                      >
                                        {/* {JSON.parse(peer.metadata).role} */}
                                        Advisor
                                      </span>
                                    </p>
                                  </Flex>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                        ) : null
                      )}
                    </Box>
                  ) : (
                    <Text
                      variant="md"
                      style={{
                        textAlign: "center",
                        marginTop: "16px",
                      }}
                    >
                      Our advisor/s seem to be on a short break, we will inform
                      them and they will be there to assist you in a jiffy, why
                      don’t you go ahead and join the call.
                    </Text>
                  )}
                </div>
              )}
            </>
          ) : null}
        </>
      ) : !error ? (
        <Loading size={100} />
      ) : null}
    </StyledVideoTile.Container>
  );
};

export default PreviewJoin;
